import React from 'react';
import PropTypes from 'prop-types';
import { defaultTemplate } from 'core/hoc';
import { ApplicationEmptyState, BaasicButton, BaasicConfirmModal, BaasicDropdown, ScrollToTopButton, BaasicDraggableModal, BaasicModal } from 'core/components';
import { PreviewLayout } from 'core/layouts';
import { ItemActionTriangle } from 'themes/components';
import { NikoFacilityOrderStatusBar, NikoFacilityOrderConfirmationModal, NikoFacilityOrderPatientTemplateNoteModal } from "application/niko-facility-order/components";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Button } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import moment from "moment";
import { observer } from "mobx-react";
import { NikoPatientInformationBar, NikoTemplateCreateModal, NikoTemplateMissingProductNoteModal, NikoPatientStatusModal, NikoPatientTemplateItemCalculationModal, NikoCreateNewPatientModal } from "application/niko-patient/components";
import { handleKeyDown, handlePaste, toDate } from "core/utils";
import { FacilityOrderTemplateNewFlowDeleteModal, FacilityOrderTemplateItemNewFlowDeleteQuestionModal } from "application/facility-order-new-flow/components";
import { NikoFacilityOrderNewFlowExtraSuppliesAddModal } from "application/niko-facility-order-inventory/components";

function NikoFacilityOrderInitialIntakePreviewTemplate({ nikoFacilityOrderInitialIntakePreviewViewStore }) {
  const {
    loaderStore,
    refreshKey,
    goToFacilityPreview,
    goToFacilityCalendar,
    tabItems,
    selectedStatusAbrv,
    setStatus,
    startInitialIntakeStatus,
    disabledByStatus,
    disabledBySendOrder,
    filterByCategory,
    itemCategoryStore,
    itemCategories,
    fetchingData,
    disabled,
    initialIntakeStatus,
    insuranceReviewStatus,
    usIntakeStatus,
    nikoPatientDetails,
    isReviewed,
    setInitialIntakeStatusComplete,
    restartInitialIntakeReview,
    nikoTemplateCreateModal,
    patientTemplateDiscontinuedModal,
    nikoPatientStatusModal,
    patientTemplateDeleteModal,
    nikoPatientTemplateItemCalculationModal,
    nikoPatientTemplateItemNewFlowDeleteQuestionModal,
    orderHistory,
    patientTemplateNoteModal,
    nikoTemplateMissingProductNoteModal,
    templateChangeConfirmModal,
    facilityOrder,
    nikoFacilityOrderNewFlowExtraSuppliesAddModal,
    isHistoryVisible,
    isInventoryStepAllowed,
    disabledByUsIntake,
    disabledByCompletion,
    nikoCreateNewPatientModal,
    openCreateNewPatientModal,
  } = nikoFacilityOrderInitialIntakePreviewViewStore;

  return (
    <PreviewLayout className="container container--new-flow" store={nikoFacilityOrderInitialIntakePreviewViewStore} emptyRenderer={<ApplicationEmptyState />} layoutFooterVisible={false} loading={loaderStore.loading}>
      <ReactTooltip positionStrategy="fixed" id="usIntakeTooltip" place="bottom" />
      {facilityOrder && facilityOrder.facility && <NikoFacilityOrderStatusBar key={refreshKey} title={`${facilityOrder.facility.name} - DOS ${toDate(facilityOrder.dateOfService)}`} pageViewStore={nikoFacilityOrderInitialIntakePreviewViewStore} isInventoryStepAllowed={isInventoryStepAllowed}>
        <div className="content__header__actions card--tertiary__data__item">
          {isHistoryVisible && <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Facility Order History" data-tooltip-id="usIntakeTooltip" onClick={() => orderHistory()}><i className="u-icon u-icon--med-lrg u-icon--us-history"></i></a>}
          <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Facility Preview" data-tooltip-id="usIntakeTooltip" onClick={() => goToFacilityPreview()}><i className="u-icon u-icon--sml u-icon--preview--dark"></i></a>
          <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Ordering Calendar" data-tooltip-id="usIntakeTooltip" onClick={() => goToFacilityCalendar()}><i className="u-icon u-icon--sml u-icon--schedules"></i></a>
        </div>
      </NikoFacilityOrderStatusBar>}
      <div className="u-flex--space-between">
        <div className="tabs__wrapper">
          <div className="tabs tabs--fullwidth">
            {tabItems.map(item =>
              <BaasicButton key={item.abrv}
                className={"tabs__item " + (item.abrv == selectedStatusAbrv ? 'active' : '')}
                label={item.name}
                onClick={() => item.abrv != selectedStatusAbrv ? setStatus(item.abrv) : {}}
              />
            )}
          </div>
        </div>
        <div className="u-flex--default u-mar--left--auto">
          {disabledByStatus && !disabledByCompletion &&
            <BaasicButton className="btn btn--base btn--primary u-mar--right--sml" disabled={disabledBySendOrder || disabled} label="Start" onClick={() => startInitialIntakeStatus()} />
          }
          <div className="multiselect__group is-category u-mar--right--sml">
            <MultiSelect
              data={itemCategoryStore.originalItems}
              textField="name"
              dataItemKey="id"
              value={itemCategories.slice()}
              filterable={false}
              placeholder="Please select"
              onChange={(e) => filterByCategory(e)}
              tags={
                itemCategories.length > 0
                  ? [
                    {
                      text: itemCategories.length == 1 ? itemCategories[0].name : `Multiple`,
                      data: itemCategories.slice(),
                    },
                  ] : []
              }
            />
          </div>
          <BaasicButton className="btn btn--base btn--primary u-mar--right--sml" icon="u-icon u-icon--xsml u-icon--add--white" label="New Patient" onClick={() => openCreateNewPatientModal()} disabled={disabledByStatus || disabledByCompletion} />
        </div>
      </div>
      {disabledByUsIntake &&
        <div className="u-mar--bottom--sml">
          {initialIntakeStatus && initialIntakeStatus.startedOn ? 'US Intake has started working on this facility. No further adjustments can be made from the Initial Intake flow.' : 'Initial Intake cannot be worked on because US Intake has already started working on this facility order.'}
        </div>
      }
      {fetchingData ?
        <div>Loading...</div>
        :
        <React.Fragment>
          {selectedStatusAbrv === "in-progress" && initialIntakeStatus && initialIntakeStatus.completedOn &&
            <div className='u-mar--bottom--tny'>
              <div className="u-mar--bottom--sml">Initial Review has been completed on {moment(initialIntakeStatus.completedOn).format("MM/DD/YYYY, hh:mm A")}</div>
            </div>
          }
          {(nikoPatientDetails && nikoPatientDetails.length > 0) ?
            nikoPatientDetails.map(nikoPatientDetail => {
              return (
                <NikoPatientTemplate
                  key={nikoPatientDetail.id}
                  nikoFacilityOrderInitialIntakePreviewViewStore={nikoFacilityOrderInitialIntakePreviewViewStore}
                  nikoPatientDetail={nikoPatientDetail}
                />
              );
            })
            :
            selectedStatusAbrv === "in-progress" &&
            <React.Fragment>
              {initialIntakeStatus && initialIntakeStatus.completedOn && !(insuranceReviewStatus && insuranceReviewStatus.startedOn) && !(usIntakeStatus && usIntakeStatus.startedOn) ?
                <div className="empty-state--facility-orders">
                  <div className="empty-state--facility-orders--table"></div>
                  <p className="empty-state--facility-orders--description">You have no orders in progress!</p>
                  <BaasicButton className="btn btn--base btn--primary" label="Restart Initial Review" disabled={disabled || disabledBySendOrder} onClick={() => restartInitialIntakeReview()} />
                </div>
                :
                <React.Fragment>
                  {isReviewed && !disabledByCompletion && itemCategories.length === 0 &&
                    <div className="empty-state--facility-orders">
                      <div className="empty-state--facility-orders--table"></div>
                      <p className="empty-state--facility-orders--description">You have no orders in progress!</p>
                      <BaasicButton
                        className="btn btn--base btn--primary"
                        label="Intake is Complete"
                        onClick={() => setInitialIntakeStatusComplete()}
                      />
                    </div>
                  }
                  {isReviewed === false && itemCategories.length == 0 &&
                    <div className="u-mar--bottom--sml">
                      All In Progress templates have been reviewed. Please review the discharged templates to complete Initial Intake.
                    </div>
                  }
                </React.Fragment>
              }
            </React.Fragment>
          }
        </React.Fragment>
      }
      <BaasicModal modalParams={nikoTemplateCreateModal} className="modal__content modal--xxlrg">
        <NikoTemplateCreateModal />
      </BaasicModal>
      <BaasicDraggableModal modalParams={patientTemplateDiscontinuedModal} className="modal__content modal--med">
        <NikoFacilityOrderConfirmationModal />
      </BaasicDraggableModal>
      <BaasicModal modalParams={nikoPatientStatusModal} className="modal__content modal--med">
        <NikoPatientStatusModal />
      </BaasicModal>
      <BaasicModal modalParams={patientTemplateDeleteModal} className="modal__content modal--med">
        <FacilityOrderTemplateNewFlowDeleteModal />
      </BaasicModal>
      <BaasicDraggableModal modalParams={nikoPatientTemplateItemCalculationModal} className="modal__content modal--lrg">
        <NikoPatientTemplateItemCalculationModal />
      </BaasicDraggableModal>
      <BaasicModal modalParams={nikoPatientTemplateItemNewFlowDeleteQuestionModal} className="modal__content modal--med">
        <FacilityOrderTemplateItemNewFlowDeleteQuestionModal />
      </BaasicModal>
      <BaasicDraggableModal modalParams={patientTemplateNoteModal} className="modal__content modal--med">
        <NikoFacilityOrderPatientTemplateNoteModal />
      </BaasicDraggableModal>
      <BaasicDraggableModal modalParams={nikoTemplateMissingProductNoteModal} className="modal__content modal--med">
        <NikoTemplateMissingProductNoteModal />
      </BaasicDraggableModal>
      <BaasicConfirmModal modalParams={templateChangeConfirmModal} />
      <BaasicModal modalParams={nikoFacilityOrderNewFlowExtraSuppliesAddModal} className="modal__content modal--xxlrg">
        <NikoFacilityOrderNewFlowExtraSuppliesAddModal />
      </BaasicModal>
      <BaasicModal modalParams={nikoCreateNewPatientModal} className="modal__content modal--xxlrg">
        <NikoCreateNewPatientModal />
      </BaasicModal>
      <ScrollToTopButton />
    </PreviewLayout>
  )
}

NikoFacilityOrderInitialIntakePreviewTemplate.propTypes = {
  nikoFacilityOrderInitialIntakePreviewViewStore: PropTypes.object.isRequired
};

const NikoPatientTemplate = observer(({ nikoFacilityOrderInitialIntakePreviewViewStore, nikoPatientDetail }) => {
  const {
    selectedStatusAbrv,
    disabledByStatus,
    disabledByCompletion,
    disabledBySendOrder,
    disabled,
    statuses,
    openNikoTemplateCreateModal,
    openNikoPatientStatusModal,
    showPreviousFlag,
    getFieldHistory
  } = nikoFacilityOrderInitialIntakePreviewViewStore;

  const TooltipContent = observer(({ initialValue, history, fieldName }) => {
    if (!history || history.fieldName !== fieldName) return null;

    const date = new Date(history.eventDate + "Z");
    const formattedDate = date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', '');

    if (fieldName === 'ImportAction') {
      return (<div>
        <div>{history.action} by {history.userName}</div>
        <div style={{ fontSize: '11px', opacity: 0.75 }}>{formattedDate}</div>
      </div>);
    }
    return (<div>
      <div>Previous: {initialValue ? initialValue : 'blank'}</div>
      <div style={{ fontSize: '11px', opacity: 0.75 }}>
        {history ? history.userName + ', ' + formattedDate : '—'}
      </div>
    </div>)
  });

  return (
    <React.Fragment>
      <ReactTooltip positionStrategy="fixed" id={"nikoPatientTooltip" + nikoPatientDetail.id} place="bottom" />
      <ReactTooltip positionStrategy="fixed" id={"registerPatientTopTip" + nikoPatientDetail.id} place="top" delayShow={650} render={() =>
        <TooltipContent initialValue={nikoPatientDetail.initialNikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail.Status} history={nikoPatientDetail.history} fieldName={'Status'} />}>
      </ReactTooltip>
      <div className="card--tertiary u-mar--bottom--sml--alt">
        <div className={`card--tertiary__header is-niko ${selectedStatusAbrv === "discharge" ? "" : "card--tertiary__header--insurance"}`}>
          <div className="u-flex--default">
            <h4 className="card--tertiary__header__title">
              {nikoPatientDetail.nikoPatient.lastName && `${nikoPatientDetail.nikoPatient.lastName}`}
              {nikoPatientDetail.nikoPatient.lastName && nikoPatientDetail.nikoPatient.firstName && ', '}
              {nikoPatientDetail.nikoPatient.firstName && `${nikoPatientDetail.nikoPatient.firstName}`}
            </h4>
            <NikoPatientInformationBar nikoPatient={nikoPatientDetail.nikoPatient} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} showVisibilityOption={false} disableEditMode={!nikoPatientDetail.isNew} />
          </div>
          {
            nikoPatientDetail.isNew &&
            <div className="status status--sml status--success">
              <p>New Patient</p>
            </div>
          }
          <div className="u-flex--default u-align--h--right">
            <BaasicButton
              className="btn btn--icon--sml-med btn--light is-plus u-mar--right--xsml"
              icon="u-icon u-icon--xmed u-icon--add-lined"
              label="Add Template"
              onlyIcon={true}
              onClick={() => { openNikoTemplateCreateModal(nikoPatientDetail) }}
              disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
            />
            <div className="segmented-control u-mar--left--xsml">
              <Button key={statuses[0].id} className={nikoPatientDetail.hospitalClassName} onClick={() => openNikoPatientStatusModal(nikoPatientDetail, statuses[0].id)} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || (nikoPatientDetail.disabled && !(selectedStatusAbrv == "in-progress" && nikoPatientDetail.status === 'Hospital'))}>{statuses[0].id}
                {nikoPatientDetail.status === "Hospital" && showPreviousFlag(nikoPatientDetail) && !nikoPatientDetail.isNew &&
                  <div className="previous-status-opacity-75" data-tooltip-id={"registerPatientTopTip" + nikoPatientDetail.id} onMouseEnter={async () => await getFieldHistory(nikoPatientDetail, 'Status', nikoPatientDetail.status)} data-tooltip-content="" ></div>}
              </Button>
              <Button key={statuses[1].id} className={nikoPatientDetail.dischargedClassName} onClick={() => openNikoPatientStatusModal(nikoPatientDetail, statuses[1].id)} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || (nikoPatientDetail.disabled && !(selectedStatusAbrv == "in-progress" && nikoPatientDetail.status === 'Hospital'))}>{statuses[1].id}
                {nikoPatientDetail.status === "Discharged" && showPreviousFlag(nikoPatientDetail) && !nikoPatientDetail.isNew &&
                  <div className="previous-status-opacity-75" data-tooltip-id={"registerPatientTopTip" + nikoPatientDetail.id} onMouseEnter={async () => await getFieldHistory(nikoPatientDetail, 'Status', nikoPatientDetail.status)} data-tooltip-content="" ></div>}
              </Button>
              <Button key={statuses[2].id} className={nikoPatientDetail.expiredClassName} onClick={() => openNikoPatientStatusModal(nikoPatientDetail, statuses[2].id)} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || (nikoPatientDetail.disabled && !(selectedStatusAbrv == "in-progress" && nikoPatientDetail.status === 'Hospital'))}>{statuses[2].id}
                {nikoPatientDetail.status === "Expired" && showPreviousFlag(nikoPatientDetail) && !nikoPatientDetail.isNew &&
                  <div className="previous-status-opacity-75" data-tooltip-id={"registerPatientTopTip" + nikoPatientDetail.id} onMouseEnter={async () => await getFieldHistory(nikoPatientDetail, 'Status', nikoPatientDetail.status)} data-tooltip-content="" ></div>}
              </Button>
              <Button key={statuses[3].id} className={nikoPatientDetail.activeClassName} onClick={() => nikoPatientDetail.status != statuses[3].id ? openNikoPatientStatusModal(nikoPatientDetail, statuses[3].id) : {}} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || (nikoPatientDetail.disabled && !(selectedStatusAbrv == "in-progress" && nikoPatientDetail.status === 'Hospital'))}>{statuses[3].id}
                {nikoPatientDetail.status === "Active" && showPreviousFlag(nikoPatientDetail) && !nikoPatientDetail.isNew &&
                  <div className="previous-status-opacity-75" data-tooltip-id={"registerPatientTopTip" + nikoPatientDetail.id} onMouseEnter={async () => await getFieldHistory(nikoPatientDetail, 'Status', nikoPatientDetail.status)} data-tooltip-content="" ></div>}
              </Button>
            </div>
          </div>
        </div>
        <div className="card--tertiary__body is-niko">
          {
            nikoPatientDetail.patientTemplates.map(patientTemplate => {
              return (
                <PatientTemplateTable
                  key={patientTemplate.id}
                  nikoFacilityOrderInitialIntakePreviewViewStore={nikoFacilityOrderInitialIntakePreviewViewStore}
                  patientTemplate={patientTemplate}
                  nikoPatientDetail={nikoPatientDetail}
                />
              );
            })
          }
        </div>
      </div>
    </React.Fragment>
  );
});

NikoPatientTemplate.propTypes = {
  nikoFacilityOrderInitialIntakePreviewViewStore: PropTypes.object.isRequired,
  nikoPatientDetail: PropTypes.object.isRequired
};

const PatientTemplateTable = observer(({ nikoFacilityOrderInitialIntakePreviewViewStore, patientTemplate, nikoPatientDetail }) => {
  const {
    disabledByStatus,
    disabledByCompletion,
    disabledBySendOrder,
    disabled,
    selectedStatusAbrv,
    inProgressStatusId,
    changeStatusId,
    noChangeStatusId,
    dischargeStatusId,
    insertImportActionId,
    deleteImportActionId,
    ostomyItemCategoryId,
    addEmptyItem,
    openPatientTemplateDiscontinuedModal,
    openPatientTemplateDeleteModal,
    reviewPatientTemplates,
    updatePatientTemplateNote,
    addNoteToTemplate,
    editItem,
    updateItem,
    openNikoPatientTemplateItemCalculationModal,
    swapPatientTemplateItem,
    setAction,
    updateImportActionId,
    deletePatientTemplateItem,
    saveItemOnProductSelect,
    getFieldHistory,
    trachTubeSubCategoryId,
    isMasterPatient
  } = nikoFacilityOrderInitialIntakePreviewViewStore;

  const TooltipContent = observer(({ initialValue, history, fieldName }) => {
    if (!history || history.fieldName !== fieldName) return null;

    const date = new Date(history.eventDate + "Z");
    const formattedDate = date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', '');

    if (fieldName === 'ImportAction') {
      return (<div>
        <div>{history.action} by {history.userName}</div>
        <div style={{ fontSize: '11px', opacity: 0.75 }}>{formattedDate}</div>
      </div>);
    }
    return (<div>
      <div>Previous: {initialValue ? initialValue : 'blank'}</div>
      <div style={{ fontSize: '11px', opacity: 0.75 }}>
        {history ? history.userName + ', ' + formattedDate : '—'}
      </div>
    </div>)
  });

  const productDropdownItemRender = (li, itemProps) => {
    const itemName = itemProps && itemProps.dataItem.description;
    const isPreferredIcon = itemProps && itemProps.dataItem.isPreferred && patientTemplate.itemCategoryId === ostomyItemCategoryId ?
      <i className="u-icon u-icon--xsml u-icon--star-outlined u-mar--right--tny"></i> : null;
    const isBackorderedIcon = itemProps && itemProps.dataItem.backordered ? <i className="u-icon u-icon--sml u-icon--backordered u-mar--left--tny"></i> : null;
    const nonBillableIcon = itemProps && !itemProps.dataItem.billableToMedicare ? <i className="u-icon u-icon--sml u-icon--non-billable u-mar--left--tny"></i> : null;
    const itemChildren = (
      <span style={{ display: "flex", flexDirection: "column", width: "100%", }}>
        <span style={{ flex: "1" }}> {li.props.children} </span>
        <span style={{ fontSize: "12px" }}> {isPreferredIcon} {itemName} {nonBillableIcon} {isBackorderedIcon}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  }

  const handleItemClick = (e) => {
    e.currentTarget.select();
  };

  const masterPatient = isMasterPatient(nikoPatientDetail);
  const dropdownRef = React.createRef();
  return (
    <React.Fragment>
      <ReactTooltip positionStrategy="fixed" id={"patientTemplateTooltip" + patientTemplate.id} place="bottom" />
      {selectedStatusAbrv !== "discharge" && !((!patientTemplate.isNew) && patientTemplate.intakeStatusId === inProgressStatusId) &&
        <div className='niko__status__wrapper '>
          {patientTemplate.isNew ?
            <div className={`u-mar--right--status-top  ${disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled ? "is-disabled" : ""}`}>
              <div className="niko__status no-bg yellow">
                <div className="niko__status__holder">
                  <Button className="niko__status__btn" icon="u-icon u-icon--xsml u-icon--box--yellow" onClick={() => { }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}>NEW ORDER</Button>
                </div>
              </div>
            </div>
            :
            <div className={` u-mar--right--status-top ${disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled ? "is-disabled" : ""}`}>
              <div className={`niko__status ${patientTemplate.intakeStatusId === noChangeStatusId ? "success" : patientTemplate.intakeStatusId === changeStatusId ? "warning" : patientTemplate.intakeStatusId === dischargeStatusId ? "danger" : ""}`}>
                <div className="niko__status__holder ">
                  <Button
                    className={`niko__status__btn ${patientTemplate.intakeStatusId === noChangeStatusId ? "success" : patientTemplate.intakeStatusId === changeStatusId ? "warning" : patientTemplate.intakeStatusId === dischargeStatusId ? "danger" : ""}`}
                    onClick={() => { }}
                    disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                  >
                    {`${patientTemplate.intakeStatusId === noChangeStatusId ? "NO CHANGES" : patientTemplate.intakeStatusId === changeStatusId ? "CHANGES" : patientTemplate.intakeStatusId === dischargeStatusId ? "DISCONTINUED" : ""}`}
                  </Button>
                </div>
              </div>
            </div>
          }
        </div>
      }
      <div className={`table--secondary__wrapper u-mar--bottom--med--condition is-niko ${trachTubeSubCategoryId == patientTemplate.subCategoryId && patientTemplate.isDiscontinued && nikoPatientDetail.status == "Active" ? 'is-add-new-disabled' : ''} ${selectedStatusAbrv === "discharge" ? "danger" : patientTemplate.isNew ? "new" : patientTemplate.intakeStatusId === noChangeStatusId ? "success" : patientTemplate.intakeStatusId === changeStatusId ? "warning-triangle" : patientTemplate.intakeStatusId === dischargeStatusId ? "danger" : ""} ${disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled || (patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some(x => !x.productId)) ? 'is-add-new-disabled' : ''}`}>
        <table className="table--secondary w--100">
          <colgroup>
            <col width={450} />
            <col width={180} />
            <col width={100} />
            <col />
            <col width={70} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={5} className="btn--group__wrapper">
                <div className="u-flex--space-between">
                  <label>{patientTemplate.subCategory.name.toUpperCase()}</label>
                  {patientTemplate.isDiscontinued && selectedStatusAbrv === "discharge" &&
                    <div>
                      <div className="u-mar--left--xsml btn--disc__wrapper">
                        Discontinued
                        <Button className="btn btn--disc" onClick={() => { openPatientTemplateDiscontinuedModal(patientTemplate) }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}>
                          <i className="u-icon u-icon--xxsml u-icon--close--neg"></i>
                        </Button>
                      </div>
                    </div>
                  }
                  {
                    <div className="card--tertiary__header__actions u-align--h--right">
                      <div className="btn--group">
                        {!patientTemplate.note && !patientTemplate.isAddingNote &&
                          <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--danger-triangle--primary" onClick={() => { addNoteToTemplate(patientTemplate) }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} data-tooltip-content="Add Note" data-tooltip-id={"patientTemplateTooltip" + patientTemplate.id} />
                        }
                        {!patientTemplate.isNew && !patientTemplate.isDiscontinued && <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--danger--primary" onClick={() => openPatientTemplateDiscontinuedModal(patientTemplate)} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} data-tooltip-content="Discontinued" data-tooltip-id={"patientTemplateTooltip" + patientTemplate.id} />}
                        {patientTemplate.isNew && <Button className="btn no-hover" icon="u-icon u-icon--delete--brand u-icon--med-lrg" onClick={() => openPatientTemplateDeleteModal(nikoPatientDetail, patientTemplate)} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} data-tooltip-content="Delete Template" data-tooltip-id={"patientTemplateTooltip" + patientTemplate.id} />}
                        <div className="btn--group__divider"></div>
                      </div>
                      {selectedStatusAbrv !== "discharge" &&
                        (patientTemplate.isInitialIntakeReviewed ?
                          <div className="status--sqare__wrapper" data-tooltip-content="Unreview" data-tooltip-id={"patientTemplateTooltip" + patientTemplate.id}>
                            <Button className="btn btn--icon btn--icon--lrg btn--primary" icon="u-icon u-icon--med u-icon--unreview" onClick={() => { reviewPatientTemplates(nikoPatientDetail, patientTemplate.id, false) }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} />
                          </div>
                          :
                          <Button className="btn btn--icon btn--icon--lrg btn--primary" icon="u-icon u-icon--med u-icon--check-list" disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} onClick={() => { reviewPatientTemplates(nikoPatientDetail, patientTemplate.id, true) }} data-tooltip-content="Review" data-tooltip-id={"patientTemplateTooltip" + patientTemplate.id} />
                        )
                      }
                    </div>
                  }
                </div>
                {(patientTemplate.note || patientTemplate.isAddingNote) &&
                  <div className={`u-mar--top--sml u-mar--bottom--xsml c-message has-input u-flex--default w--100 c-message--note${patientTemplate.intakeStatusId === dischargeStatusId ? "--discontinued" : patientTemplate.intakeStatusId === noChangeStatusId ? "--green" : patientTemplate.intakeStatusId === changeStatusId ? "--orange" : patientTemplate.isNew ? "--primary" : ""} u-mar--top--tny`}>
                    <i className="u-icon u-icon--sml u-icon--danger-triangle" />
                    <input
                      className="input input--sml input--message u-flex--grow--1 type--sml"
                      autoFocus={patientTemplate.isAddingNote}
                      onClick={(e) => e.target.select()}
                      onChange={(e) => { patientTemplate.tempNote = e.target.value }}
                      onBlur={async () => { await updatePatientTemplateNote(patientTemplate) }}
                      value={patientTemplate.tempNote}
                      disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                    />
                  </div>
                }
              </th>
            </tr>
            <tr>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
            </tr>
          </thead>
          <tbody>
            {patientTemplate.patientTemplateItems.map((item) => {
              var isFormula = item.product && (item.product.itemTypeId || (item.product.procedureCode_ && item.product.procedureCode_.abrv === "B4155"));
              if (!item.product && item.toggle) {
                setTimeout(() => {
                  if (dropdownRef.current) {
                    // v14 dropped the inner .k-dropdown-wrap — the DropDownList root element is the clickable toggle
                    const toggle = dropdownRef.current.element;
                    if (toggle) {
                      item.toggle = false;
                      toggle.click();
                    }
                  }
                }, 0);
              }
              return (
                <tr key={item.id} className={`${(!patientTemplate.isNew && item.actionId === insertImportActionId && !nikoPatientDetail.isNew) ? "new-row" : (item.actionId === deleteImportActionId ? "deleted-row" : "")} ${item.product ? "" : "is-focused"}`}>
                  <td className="table-col-input is-flex">
                    <ReactTooltip positionStrategy="fixed" id={"registerTipImportAction" + item.id} place="top" delayShow={650} render={() => <TooltipContent initialValue={null} history={item.history} fieldName={"ImportAction"} />} />
                    <ItemActionTriangle item={item} insertActionId={insertImportActionId} deleteActionId={deleteImportActionId} tooltipId={"registerTipImportAction" + item.id} onMouseEnter={async () => await getFieldHistory(item, 'ImportAction', null)} />
                    {
                      item.product ?
                        <React.Fragment>{item.product.description} <span className="table__icons-wrapper"> {!item.product.billableToMedicare && <i className="u-icon u-icon--sml u-icon--non-billable"></i>} {(item.isBackordered != null ? item.isBackordered : item.product.backordered) && <i className="u-icon u-icon--med-lrg u-icon--backordered"></i>}</span></React.Fragment>
                        :
                        <BaasicDropdown
                          placeholder="Select item name..."
                          store={patientTemplate.productStore}
                          value={item.product}
                          className="input--table"
                          itemRender={productDropdownItemRender}
                          onChange={(e) => { saveItemOnProductSelect(patientTemplate, item, e.target.value) }}
                          disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                          customRef={dropdownRef}
                        />
                    }
                  </td>
                  <td>
                    {item.product && item.product.itemId}
                  </td>
                  <td className="table-col-input text-align-left has-input">
                    <ReactTooltip positionStrategy="fixed" id={"registerTipQuantity" + item.id} place="top" delayShow={650} render={() =>
                      <TooltipContent initialValue={item.initialPatientTemplateItem && item.initialPatientTemplateItem.Quantity} history={item.history} fieldName={'Quantity'} />}>
                    </ReactTooltip>
                    {!patientTemplate.isNew && !nikoPatientDetail.isNew && item.initialPatientTemplateItem && item.initialPatientTemplateItem.Quantity != item.quantity && item.actionId !== insertImportActionId && item.isQuantityConfirmed !== false &&
                      <div className="table-col-input__notif opacity-50" data-tooltip-id={"registerTipQuantity" + item.id} onMouseEnter={async () => await getFieldHistory(item, 'Quantity', item.quantity)} data-tooltip-content="" ></div>}
                    {item.product &&
                      <input
                        className="input--table input--inherit-color no-arrows input--text-align--left w--70--px"
                        onClick={(e) => handleItemClick(e)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onChange={(e) => { editItem(item, 'quantity', parseInt(e.target.value)) }}
                        onBlur={() => { updateItem(item, patientTemplate) }}
                        value={item.quantity || item.quantity === 0 ? item.quantity : undefined}
                        type="number"
                        min="0"
                        disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled || masterPatient}
                      />
                    }

                  </td>
                  <td className="table-col-input">
                    <ReactTooltip positionStrategy="fixed" id={"registerTipNote" + item.id} place="top" delayShow={650} render={() =>
                      <TooltipContent initialValue={item.initialPatientTemplateItem && item.initialPatientTemplateItem.Note} history={item.history} fieldName={'Note'} />}>
                    </ReactTooltip>
                    {!patientTemplate.isNew && !nikoPatientDetail.isNew && item.initialPatientTemplateItem && item.initialPatientTemplateItem.Note != item.note && item.actionId !== insertImportActionId && item.isNoteConfirmed !== false &&
                      <div className="table-col-input__notif opacity-50" data-tooltip-id={"registerTipNote" + item.id} onMouseEnter={async () => await getFieldHistory(item, 'Note', item.note)} data-tooltip-content="" ></div>}
                    {isFormula && !masterPatient ?
                      <input
                        className="input--table input--inherit-color no-arrows input--text-align--left w--100 u-mar--left--tny"
                        onClick={() => openNikoPatientTemplateItemCalculationModal(item, patientTemplate)}
                        value={item.note}
                        readOnly={true}
                        disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                      />
                      :
                      <input
                        className="input--table input--inherit-color no-arrows input--text-align--left w--100 u-mar--left--tny"
                        onClick={(e) => e.target.select()}
                        onChange={(e) => { editItem(item, 'note', e.target.value) }}
                        onBlur={() => { updateItem(item, patientTemplate) }}
                        value={item.note}
                        disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled || !item.product}
                        readOnly={false}
                      />
                    }
                  </td>
                  <td className="type--center is-hidden">
                    <div className="table__icons">
                      {item.actionId !== insertImportActionId && !(trachTubeSubCategoryId == patientTemplate.subCategoryId && patientTemplate.isDiscontinued && nikoPatientDetail.status == "Active") &&
                        <Button
                          disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                          className="btn btn--icon"
                          icon="u-icon u-icon--update-table u-icon--sml"
                          label="Change"
                          title={"Swap"}
                          onClick={() => swapPatientTemplateItem(item, patientTemplate, nikoPatientDetail)}
                        ></Button>
                      }
                      {item.actionId === deleteImportActionId ?
                        <Button
                          className="btn btn--icon is-delete"
                          icon="u-icon u-icon--sml u-icon--undo--table"
                          label="Undelete"
                          onClick={() => setAction(item, updateImportActionId, patientTemplate)}
                          disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                          title={"Undo"}
                        ></Button>
                        :
                        <Button
                          className="btn btn--icon is-delete"
                          icon={`u-icon u-icon--sml ${!item.product ? "u-icon--close--table" : "u-icon--delete--primary"}`}
                          onClick={() => { deletePatientTemplateItem(item, patientTemplate, nikoPatientDetail) }}
                          disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                          title={!item.product ? "Cancel" : "Delete"}
                        ></Button>
                      }
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {patientTemplate.patientTemplateItems && !patientTemplate.patientTemplateItems.some(x => !x.productId) &&
          <button className={`table--secondary__add-button${(disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled) ? ' is-disabled' : ''}`} onClick={() => { addEmptyItem(patientTemplate) }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}>
            <span></span>
          </button>
        }
      </div>
    </React.Fragment>
  );
});

PatientTemplateTable.propTypes = {
  nikoFacilityOrderInitialIntakePreviewViewStore: PropTypes.object.isRequired,
  patientTemplate: PropTypes.object.isRequired,
  nikoPatientDetail: PropTypes.object.isRequired
};

export default defaultTemplate(NikoFacilityOrderInitialIntakePreviewTemplate);
